// Phase 6.0a.1 — first-run setup wizard.
//
// Mirrors LoginScreen's visual treatment (same dark card,
// same serif title, same mono labels, same input chrome)
// because this IS the operator's first impression of the
// system. The framing is "welcome / one-time setup" rather
// than "sign in," but the design language is identical so
// the transition into /login after submission feels
// continuous.
//
// Inline validation rules (per the brief):
//   * Username: [a-z0-9_-]{3,32}
//   * Password: minimum 8 chars
//   * Confirm: exact match with password
//
// Submit is disabled until all three fields satisfy client-
// side validation. The server runs the SAME checks (and
// returns 422 with field-shaped errors) so anything that
// slips past the client validation still surfaces inline.

import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  ApiError,
  postSetup,
  type SetupValidationErrors,
} from "../lib/api";
import { useAuth } from "../state/AuthContext";
import { useSetupStatus } from "../state/SetupStatusContext";

const USERNAME_RE = /^[a-z0-9_-]{3,32}$/;
// Match the server-side regex in users/email.py exactly. Local
// part allows alnum + . _ + -; domain requires alnum + at least
// one dot-separated TLD.
const EMAIL_RE = /^[a-z0-9._+\-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const USERNAME_HINT =
  "3–32 chars: lowercase letters, digits, hyphen, underscore.";
const EMAIL_HINT = "Used for Slack workspace attribution.";
const PASSWORD_HINT = "At least 8 characters.";

interface FieldErrors {
  username?: string;
  email?: string;
  password?: string;
  password_confirm?: string;
}

function validate(
  username: string,
  email: string,
  password: string,
  passwordConfirm: string,
): FieldErrors {
  const out: FieldErrors = {};
  const lowered = username.trim().toLowerCase();
  if (lowered && !USERNAME_RE.test(lowered)) {
    out.username = USERNAME_HINT;
  }
  const emailLowered = email.trim().toLowerCase();
  if (emailLowered && !EMAIL_RE.test(emailLowered)) {
    out.email = "Enter a valid email address.";
  }
  if (password && password.length < 8) {
    out.password = PASSWORD_HINT;
  }
  if (passwordConfirm && password !== passwordConfirm) {
    out.password_confirm = "Passwords do not match.";
  }
  return out;
}

export function SetupPage() {
  const navigate = useNavigate();
  const { login: setAuth } = useAuth();
  const { markSetupDone } = useSetupStatus();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverErrors, setServerErrors] = useState<SetupValidationErrors | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const clientErrors = useMemo(
    () => validate(username, email, password, passwordConfirm),
    [username, email, password, passwordConfirm],
  );

  const canSubmit =
    !busy &&
    username.trim().length >= 3 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    passwordConfirm.length > 0 &&
    Object.keys(clientErrors).length === 0;

  const errorFor = (field: keyof FieldErrors): string | undefined => {
    return serverErrors?.[field] ?? clientErrors[field];
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setServerErrors(null);
    setGlobalError(null);

    try {
      const r = await postSetup({
        username: username.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        password,
        password_confirm: passwordConfirm,
      });
      // Hand the new superadmin's token straight to AuthContext
      // so the next render lands on /chooser already
      // authenticated. Also flip the setup-required flag
      // synchronously so the routing guard immediately unblocks.
      markSetupDone();
      setAuth({ token: r.token, expires_at: r.expires_at });
      navigate("/chooser", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // Field-shaped errors: {"errors": {"field": "msg", ...}}
        const body = err.responseBody as
          | { detail?: { errors?: SetupValidationErrors } }
          | null
          | undefined;
        const errors = body?.detail?.errors;
        if (errors && typeof errors === "object") {
          setServerErrors(errors);
        } else {
          setGlobalError("Setup rejected by server. Check your input.");
        }
      } else if (err instanceof ApiError && err.status === 410) {
        setGlobalError(
          "Setup has already been completed on this server. Please sign in instead.",
        );
      } else if (err instanceof ApiError && err.status === 0) {
        setGlobalError("Cannot reach the service. Is it running?");
      } else if (err instanceof Error) {
        setGlobalError(`Setup failed: ${err.message}`);
      } else {
        setGlobalError("Setup failed");
      }
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="setup-page"
      className="h-screen flex flex-col items-center justify-center bg-theme-bg-app p-6"
    >
      <div className="w-full max-w-md">
        <header className="mb-8 text-center">
          <h1 className="text-display">
            Welcome
          </h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-theme-text-tertiary">
            Set up your superadmin account
          </p>
          <p className="mt-4 font-mono text-xs text-theme-text-secondary leading-relaxed">
            This is a one-time setup. After you create the first
            account, this screen will not appear again.
          </p>
        </header>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="setup-form">
          <Field
            id="username"
            label="Username"
            value={username}
            onChange={setUsername}
            disabled={busy}
            autoComplete="username"
            autoFocus
            error={errorFor("username")}
            hint={USERNAME_HINT}
            testid="setup-username"
          />
          <Field
            id="email"
            type="email"
            label="Email"
            value={email}
            onChange={setEmail}
            disabled={busy}
            autoComplete="email"
            error={errorFor("email")}
            hint={EMAIL_HINT}
            testid="setup-email"
          />
          <Field
            id="password"
            type="password"
            label="Password"
            value={password}
            onChange={setPassword}
            disabled={busy}
            autoComplete="new-password"
            error={errorFor("password")}
            hint={PASSWORD_HINT}
            testid="setup-password"
          />
          <Field
            id="password_confirm"
            type="password"
            label="Confirm password"
            value={passwordConfirm}
            onChange={setPasswordConfirm}
            disabled={busy}
            autoComplete="new-password"
            error={errorFor("password_confirm")}
            testid="setup-password-confirm"
          />

          {globalError && (
            <div
              data-testid="setup-global-error"
              role="alert"
              className="flex items-start gap-2 px-3 py-2 bg-theme-error-bg border border-theme-error-border text-theme-error-text text-xs"
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden />
              <span>{globalError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="setup-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-theme-bg-card border border-theme-border-default hover:border-theme-text-primary font-mono text-xs uppercase tracking-widest text-theme-text-primary transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-theme-border-default"
          >
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} aria-hidden />
                Creating…
              </>
            ) : (
              <>
                Create account
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
              </>
            )}
          </button>

          {/* Visible fallback link, only meaningful when setup
              is already done (the routing guard normally hides
              /setup in that case; the link is here as defense
              in depth + as the link the 410 message recommends). */}
          <div className="text-center">
            <Link
              to="/login"
              data-testid="setup-login-link"
              className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150"
            >
              Already set up? Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  error?: string;
  hint?: string;
  testid?: string;
}

function Field({
  id, label, value, onChange,
  type = "text",
  disabled = false,
  autoComplete,
  autoFocus = false,
  error,
  hint,
  testid,
}: FieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary mb-1.5"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        data-testid={testid}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={
          error ? `${id}-error` : (hint ? `${id}-hint` : undefined)
        }
        className={[
          "w-full px-3 py-2 bg-theme-bg-card border font-mono text-sm",
          "text-theme-text-primary placeholder-theme-text-tertiary",
          "focus:outline-none disabled:opacity-50",
          error
            ? "border-theme-error-border focus:border-theme-error-border"
            : "border-theme-border-default focus:border-theme-border-active",
        ].join(" ")}
      />
      {error ? (
        <p
          id={`${id}-error`}
          data-testid={testid ? `${testid}-error` : undefined}
          className="mt-1 font-mono text-[10px] text-theme-error-text"
        >
          {error}
        </p>
      ) : hint ? (
        <p
          id={`${id}-hint`}
          className="mt-1 font-mono text-[10px] text-theme-text-tertiary"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
